import React, { useState, useEffect, useRef } from "react";
import { ConstructorEventUpdateSQLStatus } from "../../../Event/event";
import "../../../assets/css/newacitivity.css";

export default function GuessNumberGame() {
  const [currentNumber, setCurrentNumber] = useState(0);
  const [input, setInput] = useState("");
  const [feedback, setFeedback] = useState("");
  const [feedbackClass, setFeedbackClass] = useState("");
  const [clue, setClue] = useState("");
  const [showReset, setShowReset] = useState(false);
  const [inactive, setInactive] = useState(false); // 👈 trigger collapse effect
  const timerRef = useRef(null);

  const placeValues = [
    { name: "hundreds", digit: 0 },
    { name: "tens", digit: 0 },
    { name: "ones", digit: 0 },
  ];

  const generateNumber = () => {
    let digits = [];
    digits.push(Math.floor(Math.random() * 9) + 1); // Hundreds (1-9)
    digits.push(Math.floor(Math.random() * 10)); // Tens (0-9)
    digits.push(Math.floor(Math.random() * 10)); // Ones (0-9)
    return parseInt(digits.join(""), 10);
  };

  const buildClue = (number) => {
    const digitsStr = number.toString().padStart(3, "0");
    placeValues[0].digit = parseInt(digitsStr[0], 10);
    placeValues[1].digit = parseInt(digitsStr[1], 10);
    placeValues[2].digit = parseInt(digitsStr[2], 10);

    let parts = placeValues
      .filter((pv) => pv.digit !== 0)
      .map((pv) => `The number has ${pv.digit} ${pv.name}`);

    if (parts.length === 1) return parts[0] + ".";
    return parts.slice(0, -1).join(", ") + " and " + parts.slice(-1) + ".";
  };

  const setupGame = () => {
    const number = generateNumber();
    setCurrentNumber(number);
    setClue(buildClue(number));
    setFeedback("");
    setFeedbackClass("");
    setInput("");
    setShowReset(false);
    resetTimer();
  };

  // 🎤 Speak
  const speak = (text) => {
    if ("speechSynthesis" in window) {
      const msg = new SpeechSynthesisUtterance(text);
      msg.pitch = 1;
      msg.rate = 1;
      msg.voice = speechSynthesis.getVoices()[0];
      window.speechSynthesis.cancel();
      window.speechSynthesis.speak(msg);
    }
  };

  useEffect(() => {
    setupGame();
  }, []);

  useEffect(() => {
    if (clue) speak(clue);
  }, [clue]);

  useEffect(() => {
    if (feedback) speak(feedback);
  }, [feedback]);

  // 🕒 Timer to detect inactivity
  const resetTimer = () => {
    clearTimeout(timerRef.current);
    setInactive(false);
    timerRef.current = setTimeout(() => {
      setInactive(true); // trigger collapse after 20s
    }, 20000);
  };

  const successMessages = [
  "Super! Well done!",
  " Great job, you nailed it!",
  " Awesome, you got it!",
  " Fantastic! Keep it up!",
  " Perfect! That’s correct!",
  " Yay, you cracked the code!"
];

const checkAnswer = () => {
  if (!input.match(/^\d+$/)) {
    setFeedback("Please enter a valid number.");
    setFeedbackClass("incorrect");
    resetTimer();
    return;
  }

  if (parseInt(input, 10) === currentNumber) {
    // 👉 pick a random message
    const randomMsg =
      successMessages[Math.floor(Math.random() * successMessages.length)];
    setFeedback(randomMsg);
    setFeedbackClass("correct");

    const responsedata = ConstructorEventUpdateSQLStatus();
    console.log("Event completed", responsedata);
    setShowReset(true);
  } else {
    setFeedback(" That’s not correct. Try again!");
    setFeedbackClass("incorrect");
  }
  resetTimer();
};


  return (
    <div
      className={`game-container ${inactive ? "collapsed" : ""}`} // 👈 collapse effect
      style={{
        background: "linear-gradient(135deg,rgb(55, 207, 245) 0%,rgb(72, 89, 241) 100%)",
        fontFamily: "Fredoka One, cursive",
        height: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        transition: "transform 0.6s ease, opacity 0.6s ease",
      }}
      onClick={resetTimer}
      onKeyDown={resetTimer}
    >
      <div
        style={{
          background: "#fff",
          padding: "30px 40px",
          borderRadius: 20,
          boxShadow: "0 12px 30px rgba(255, 123, 0, 0.25)",
          maxWidth: 420,
          width: "100%",
          textAlign: "center",
        }}
      >
        <h2 style={{ color: "#e65100", marginBottom: 20 }}>
          Guess the Number
        </h2>

        {/* 🎭 Character Avatar */}
        <img
          src={
            feedbackClass === "correct"
              ? "https://avataaars.io/?avatarStyle=Circle&topType=ShortHair&eyeType=Happy&mouthType=Smile"
              : feedbackClass === "incorrect"
              ? "https://avataaars.io/?avatarStyle=Circle&topType=ShortHair&eyeType=Cry&mouthType=Sad"
              : "https://avataaars.io/?avatarStyle=Circle&topType=ShortHair&eyeType=Default&mouthType=Smile"
          }
          alt="character"
          style={{ width: 120, height: 120, margin: "0 auto 20px" }}
        />

        <div
          style={{
            fontSize: "1.6rem",
            color: "#bf360c",
            marginBottom: 25,
            minHeight: 60,
          }}
          dangerouslySetInnerHTML={{ __html: clue }}
        ></div>

        <input
          type="text"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") checkAnswer();
          }}
          style={{
            fontSize: "1.5rem",
            padding: "8px 15px",
            borderRadius: 12,
            border: "3px solid #004d40",
            width: "100%",
            textAlign: "center",
            outline: "none",
            marginBottom: 20,
          }}
        />

        <div>
          {!showReset && (
            <button
              onClick={checkAnswer}
              style={{
                marginTop: 25,
                padding: "12px 30px",
                borderRadius: 25,
                background: "#ff5722",
                color: "white",
                fontSize: "1.3rem",
                cursor: "pointer",
                border: "none",
                boxShadow: "0 6px 18px rgba(255, 87, 34, 0.6)",
              }}
            >
              Submit
            </button>
          )}
          {showReset && (
            <button
              onClick={setupGame}
              style={{
                marginTop: 20,
                padding: "10px 20px",
                borderRadius: 25,
                background: "#fb8c00",
                color: "white",
                fontSize: "1rem",
                cursor: "pointer",
                border: "none",
                boxShadow: "0 6px 18px rgba(251, 140, 0, 0.6)",
                width: "fit-content",
                marginLeft: "auto",
                marginRight: "auto",
                display: "block",
              }}
            >
              New Number
            </button>
          )}
        </div>

        <div
          style={{
            marginTop: 25,
            fontSize: "1.4rem",
            minHeight: 40,
            fontWeight: 700,
            color: feedbackClass === "correct" ? "#2e7d32" : "#b71c1c",
          }}
        >
          {feedback}
        </div>
      </div>
    </div>
  );
}
